import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import ProjectTypeModel from '../../models/ProjectTypeModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult, Result } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';
import ProjectType_tab_Milestones from './Tabs/ProjectType_tab_Milestones';
import ProjectType_tab_UserDefinedFields from './Tabs/ProjectType_tab_UserDefinedFields';
import ProjectType_tab_WorkflowDefinitions from './Tabs/ProjectType_tab_WorkflowDefinitions';
import ProjectType_tab_WorkflowRoles from './Tabs/ProjectType_tab_WorkflowRoles';

const ProjectTypeView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] })
    const { handleResult, handleException } = useNotifications()
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true)
    const navigate = useNavigate()

    const [formData, setFormData] = useState<ProjectTypeModel>({
        id: 0,
        name: '',
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        fields: [],
        concurrencyId: ''
    });

    let idParams: IdParams = useParams();
    let projectTypeId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        projectTypeId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (projectTypeId !== undefined) {
            DataService.getById<ProjectTypeModel>('api/v1/projecttypes/', projectTypeId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [projectTypeId]);

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<number>(`api/v1/projecttypes/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            navigate('/plm/workflowsetup/projectType/' + response.data)
                    })
                    .catch(e => handleException(e));
            else 
                DataService
                    .update<ProjectTypeModel>(`api/v1/projecttypes/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (projectTypeId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.ViewProjectType)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Project type' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditProjectType}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                </Container>
            </Form>
            {projectTypeId && <Container fluid>
                <Row>
                    <Col md='12'>
                        <HorizontalTabs
                            id='vtabs'
                            defaultActiveKey='milestones'
                            tabs={[
                                {
                                    title: 'Milestones',
                                    eventKey: 'milestones',
                                    component: <ProjectType_tab_Milestones projectTypeId={projectTypeId} />,
                                },
                                {
                                    title: 'User-defined fields',
                                    eventKey: 'userdefinedfields',
                                    component: <ProjectType_tab_UserDefinedFields projectTypeId={projectTypeId} />,
                                },
                                {
                                    title: 'Workflows',
                                    eventKey: 'workflowDefinitions',
                                    component: <ProjectType_tab_WorkflowDefinitions projectTypeId={projectTypeId} />,
                                },
                                {
                                    title: 'Workflow roles',
                                    eventKey: 'workflowRoles',
                                    component: <ProjectType_tab_WorkflowRoles projectTypeId={projectTypeId} />,
                                },
                            ]}
                        />
                    </Col>
                </Row>
            </Container>}
        </div>
    );
}
export default ProjectTypeView;